import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

// Contexts
import { AuthProvider } from "./contexts/AuthContext";
import { NotificationProvider } from "./contexts/NotificationContext";

// Components
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import RoleBasedHome from "./components/RoleBasedHome";

// Pages
import LoginPage from "./pages/LoginPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import SetPasswordPage from "./pages/SetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import StaffMyDayPage from "./pages/StaffMyDayPage";
import StaffJobHubPage from "./pages/StaffJobHubPage";
import BookingsPage from "./pages/BookingsPage";
import BookingDetailPage from "./pages/BookingDetailPage";
import NewBookingPage from "./pages/NewBookingPage";
import EditBookingPage from "./pages/EditBookingPage";
import ClientsPage from "./pages/ClientsPage";
import ClientDetailPage from "./pages/ClientDetailPage";
import NewClientPage from "./pages/NewClientPage";
import EditClientPage from "./pages/EditClientPage";
import CalendarPage from "./pages/CalendarPage";
import ChatPage from "./pages/ChatPage";
import TacoChatsPage from "./pages/TacoChatsPage";
import AIAssistantPage from "./pages/AIAssistantPage";
import NotificationsPage from "./pages/NotificationsPage";
import { AdminPage } from "./pages/admin";
import SettingsPage from "./pages/SettingsPage";
import OperationsDashboardPage from "./pages/OperationsDashboardPage";
import PropertyLookupPage from "./pages/PropertyLookupPage";

// Client Portal Pages
import { 
  ClientPortalLogin, 
  ClientPortalDashboard, 
  ClientPortalJobDetail 
} from "./pages/portal";

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NotificationProvider>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/set-password" element={<SetPasswordPage />} />
            
            {/* Client Portal (Public - separate auth) */}
            <Route path="/portal/login" element={<ClientPortalLogin />} />
            <Route path="/portal" element={<ClientPortalDashboard />} />
            <Route path="/portal/booking/:bookingId" element={<ClientPortalJobDetail />} />
            
            {/* Password reset (requires auth but not full app) */}
            <Route
              path="/reset-password"
              element={
                <ProtectedRoute>
                  <ResetPasswordPage />
                </ProtectedRoute>
              }
            />

            {/* Operations Dashboard - Default for admins/directors */}
            <Route
              path="/operations"
              element={
                <ProtectedRoute>
                  <Layout>
                    <OperationsDashboardPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Protected routes with Layout */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Layout>
                    <DashboardPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Staff My Day - Default for staff */}
            <Route
              path="/my-day"
              element={
                <ProtectedRoute>
                  <Layout>
                    <StaffMyDayPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Staff Job Hub - Job details for staff */}
            <Route
              path="/my-jobs/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <StaffJobHubPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Bookings */}
            <Route
              path="/bookings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BookingsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/bookings/new"
              element={
                <ProtectedRoute requiredRoles={["director", "manager"]}>
                  <Layout>
                    <NewBookingPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/bookings/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BookingDetailPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/bookings/:id/edit"
              element={
                <ProtectedRoute requiredRoles={["director", "manager"]}>
                  <Layout>
                    <EditBookingPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Clients */}
            <Route
              path="/clients"
              element={
                <ProtectedRoute requiredRoles={["director", "manager"]}>
                  <Layout>
                    <ClientsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/clients/new"
              element={
                <ProtectedRoute requiredRoles={["director", "manager"]}>
                  <Layout>
                    <NewClientPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/clients/:id"
              element={
                <ProtectedRoute requiredRoles={["director", "manager"]}>
                  <Layout>
                    <ClientDetailPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/clients/:id/edit"
              element={
                <ProtectedRoute requiredRoles={["director", "manager"]}>
                  <Layout>
                    <EditClientPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Calendar */}
            <Route
              path="/calendar"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CalendarPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Chat */}
            <Route
              path="/chat"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ChatPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* AI Assistant (Available to all - role-based responses) */}
            <Route
              path="/assistant"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AIAssistantPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Taco Chats - Multi-Thread Chat (Phase 5) */}
            <Route
              path="/chats"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TacoChatsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/chats/:conversationId"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TacoChatsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Notifications */}
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <Layout>
                    <NotificationsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Admin */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute requireAdmin>
                  <Layout>
                    <AdminPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Settings */}
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <SettingsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Property Lookup - Internal Tool */}
            <Route
              path="/property-lookup"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PropertyLookupPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Default redirect - role-based */}
            <Route path="/" element={
              <ProtectedRoute>
                <RoleBasedHome />
              </ProtectedRoute>
            } />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>

          {/* Global Toast Notifications */}
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: '#18181b',
                color: '#fff',
                border: 'none',
              },
            }}
          />
        </NotificationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
